"use client";

import { useState, useEffect, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getMorningBrief, logMood, updateTask } from "@/lib/api";
import { useDataRefresh } from "@/lib/store";
import type { MorningBrief } from "@/lib/api";
import type { CarryForwardSummary } from "@/lib/carryForwardSummary";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";

const MOOD_EMOJIS: Array<{ score: number; emoji: string; label: string }> = [
  { score: 1, emoji: "😞", label: "Low" },
  { score: 2, emoji: "😕", label: "Off" },
  { score: 3, emoji: "😐", label: "OK" },
  { score: 4, emoji: "😊", label: "Good" },
  { score: 5, emoji: "😄", label: "Great" },
];

function firstSentence(text: string): string {
  // Trim the AI narrative to its first sentence so the popup stays light.
  const trimmed = text.trim();
  const match = trimmed.match(/^[^.!?]*[.!?]/);
  return match ? match[0].trim() : trimmed;
}

interface Props {
  onDismiss: () => void;
  onDetails: (summary?: CarryForwardSummary | null) => void;
  onReviewToday?: (summary: CarryForwardSummary) => void;
  // Personalisation hooks: first-name greeting + streak chip. Both
  // optional so the popup gracefully falls back when the parent doesn't
  // have the projection yet.
  displayName?: string | null;
  currentStreak?: number;
}

function firstName(displayName?: string | null): string | null {
  if (!displayName) return null;
  const trimmed = displayName.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0];
}

export default function MorningBriefPopup({
  onDismiss,
  onDetails,
  onReviewToday: _onReviewToday,
  displayName,
  currentStreak = 0,
}: Props) {
  const [brief, setBrief] = useState<MorningBrief | null>(null);
  const [mood, setMood] = useState(3);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    getMorningBrief()
      .then(setBrief)
      .catch(() => setFailed(true));
  }, []);

  const carryForwardSummary = brief?.carry_forward_summary;
  const hasCarryForwardSummary = (carryForwardSummary?.total || 0) > 0;

  // Local optimistic copy of the leftover task ids so ✓ Done removes the
  // row immediately; the underlying server state catches up via updateTask.
  const [completedLeftovers, setCompletedLeftovers] = useState<Set<string>>(new Set());
  const remainingLeftovers = (carryForwardSummary?.tasks ?? []).filter(
    (task) => !completedLeftovers.has(task.id),
  );

  const persistCheckIn = () => {
    // Energy slider was removed (no real energy tracking surface yet); we
    // still send a neutral default so the MoodEntry row stays well-formed.
    logMood({ mood_score: Math.round(mood), energy_level: 3 }).catch(() => {});
  };

  const handleDismiss = () => {
    persistCheckIn();
    onDismiss();
  };

  // "Reshape day" — only used when carry-forward exists and the user wants
  // to plan rather than execute. Routes to the Plan tab via onDetails.
  const handleReshape = () => {
    persistCheckIn();
    onDetails(carryForwardSummary ?? null);
  };

  const handleCompleteLeftover = async (taskId: string) => {
    setCompletedLeftovers((current) => {
      const next = new Set(current);
      next.add(taskId);
      return next;
    });
    try {
      await updateTask(taskId, { status: "completed" });
      // Backend cascades the completion (ADR-0005); fire notifiers so Today /
      // Goals / Progress reflect it without waiting for a tab remount.
      useDataRefresh.getState().notifyTaskChange();
      useDataRefresh.getState().notifyGoalChange();
    } catch {
      // Rollback the optimistic mark so the user can retry.
      setCompletedLeftovers((current) => {
        const next = new Set(current);
        next.delete(taskId);
        return next;
      });
    }
  };

  // Overlay is hoisted out of the function body (see PopupOverlay below)
  // so its component identity stays stable across mood-tap renders, which
  // would otherwise cause AnimatePresence to replay the entrance.

  // Empty state: no tasks/goals yet — show setup prompt instead of vanishing
  if (failed || (brief && !hasCarryForwardSummary && brief.pending_count === 0 && (!brief.top_tasks || brief.top_tasks.length === 0))) {
    return (
      <PopupOverlay onDismiss={onDismiss}>
        <div data-testid="morning-brief-empty" className="relative">
          <div className="text-center">
            <span className="text-3xl">🌅</span>
            <h2 id="morning-brief-title" className="mt-2 font-serif text-2xl font-medium tracking-tight text-text">Good morning</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Set up your first goal to get personalized morning briefs.
            </p>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              onClick={onDismiss}
              className="flex-1 rounded-xl py-2.5 text-sm font-medium text-muted transition-colors hover:bg-secondary/50"
            >
              Later
            </button>
            <button
              onClick={() => onDetails(null)}
              className="flex-1 rounded-xl bg-forest py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-med"
            >
              Set up
            </button>
          </div>
        </div>
      </PopupOverlay>
    );
  }

  if (!brief) return null;

  const topTask = brief.top_tasks?.[0];
  // Prefer the LLM narrative when available; fall back to the statistical
  // pattern-detector insight so the body still has a warm line.
  const narrative = brief.ai_brief?.trim() || brief.insight?.trim() || null;

  return (
    <PopupOverlay onDismiss={onDismiss}>
      <div data-testid="morning-brief-popup" className="relative">
      <div className="text-center mb-4">
        <span className="text-3xl">🌅</span>
        <h2 id="morning-brief-title" className="mt-2 font-serif text-2xl font-medium tracking-tight text-text">
          {firstName(displayName) ? `Good morning, ${firstName(displayName)}` : "Good morning"}
        </h2>
        {currentStreak > 0 && (
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber/15 px-2.5 py-1 text-[11px] font-semibold text-amber">
            <span aria-hidden="true">🔥</span>
            <span>
              {currentStreak}-day streak
            </span>
          </div>
        )}
      </div>

      {narrative && (
        <p className="mb-4 text-center text-sm leading-relaxed text-text-light">
          {sanitizeSurfaceTitle(firstSentence(narrative))}
        </p>
      )}

      {topTask && (
        <div className="mb-4 rounded-xl bg-secondary/30 p-3">
          <p className="mb-1 text-xs font-medium text-muted">Top priority</p>
          <p className="text-sm font-semibold text-text">{sanitizeSurfaceTitle(topTask.title)}</p>
          {topTask.estimated_minutes && (
            <p className="mt-0.5 text-xs text-muted">{topTask.estimated_minutes} min</p>
          )}
        </div>
      )}

      {remainingLeftovers.length > 0 && (
        <div className="mb-4" data-testid="morning-carry-forward-leftovers">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
            {remainingLeftovers.length} thing{remainingLeftovers.length === 1 ? "" : "s"} waited for you
          </p>
          <ul className="space-y-1.5">
            {remainingLeftovers.map((task) => (
              <li
                key={task.id}
                className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2"
              >
                <button
                  type="button"
                  onClick={() => void handleCompleteLeftover(task.id)}
                  aria-label={`Mark ${task.title} done`}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border text-muted transition-colors hover:border-forest hover:bg-forest hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med/40"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <span className="min-w-0 flex-1 truncate text-sm text-text">
                  {sanitizeSurfaceTitle(task.title)}
                </span>
                {task.estimated_minutes ? (
                  <span className="shrink-0 text-[10px] text-muted">{task.estimated_minutes}m</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-4">
        <p className="mb-2 text-center text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
          Mood
        </p>
        <div className="flex items-center justify-between gap-1">
          {MOOD_EMOJIS.map((option) => {
            const active = mood === option.score;
            return (
              <motion.button
                key={option.score}
                type="button"
                onClick={() => setMood(option.score)}
                aria-label={option.label}
                aria-pressed={active}
                whileTap={{ scale: 0.85 }}
                animate={{ scale: active ? 1.15 : 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 18 }}
                className={`flex h-11 w-11 items-center justify-center rounded-full text-2xl ${
                  active
                    ? "bg-forest-med/10 ring-2 ring-forest-med/40"
                    : "opacity-60 hover:opacity-100 hover:bg-secondary/40"
                }`}
              >
                {option.emoji}
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3">
        {hasCarryForwardSummary && (
          <button
            onClick={handleReshape}
            className="flex-1 rounded-xl border border-border py-2.5 text-sm font-medium text-text transition-colors hover:border-forest/25 hover:text-forest-med"
          >
            Reshape day
          </button>
        )}
        <button
          onClick={handleDismiss}
          className={`${hasCarryForwardSummary ? "flex-1" : "w-full"} rounded-xl bg-forest py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-med`}
        >
          Start now
        </button>
      </div>
      </div>
    </PopupOverlay>
  );
}

interface PopupOverlayProps {
  onDismiss: () => void;
  children: ReactNode;
}

// Hoisted out of MorningBriefPopup so its identity stays stable across
// state changes (mood tap, leftover-done, etc.). Defining it inside the
// parent caused AnimatePresence to remount → entrance animation replayed
// on every render.
function PopupOverlay({ onDismiss, children }: PopupOverlayProps) {
  return (
    <AnimatePresence>
      <motion.div
        key="morning-brief-overlay"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 backdrop-blur-md px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="morning-brief-title"
        onClick={onDismiss}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
      >
        <motion.div
          className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-border bg-gradient-to-b from-sand-pale via-cream to-cream px-5 py-5 shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ type: "spring", stiffness: 220, damping: 22, mass: 0.6 }}
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-amber/10 via-amber/5 to-transparent" />
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="absolute right-4 top-4 z-10 flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-secondary hover:text-text"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          <div className="relative">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
